import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, groupBy, map, mapTo, mergeMap, switchMap } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../global-message';
import { HttpResponseStatus } from '../../../global-message/models/response-status.model';
import { GlobalMessageActions } from '../../../global-message/store/actions';
import { ShoppingList, UpdatePermissionType } from '../../../model';
import { CurrenciesActionTypes } from '../../../site-context/store/actions/currencies.action';
import { LanguagesActionTypes } from '../../../site-context/store/actions/languages.action';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { ShoppingListConnector } from '../../connectors/shopping-list-connector';
import { PrincipalConfigurationService } from '../../index';
import { UserActions } from '../actions';
import { ShoppingListEntryActionTypes } from '../actions/shopping-list-entry.action';
import { ShoppingListActionTypes } from '../actions/shopping-list.action';
import { ShoppingListsSelectors } from '../selectors/user.selectors';
import { StateWithUser } from '../user-state';

@Injectable()
export class ShoppingListEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(CurrenciesActionTypes.CurrencyChange, LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Logout)
  );
  private logoutOnlyContextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  searchMiniShoppingLists$: Observable<UserActions.SearchMiniShoppingListsSuccess | UserActions.SearchMiniShoppingListsFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingListActionTypes.SearchMiniShoppingLists),
        map((action: UserActions.SearchMiniShoppingLists) => action.payload),
        switchMap((payload) => {
          return this.connector.search(payload.userId, payload.searchParams).pipe(
            map((data) => new UserActions.SearchMiniShoppingListsSuccess({ ...payload, searchResult: data })),
            catchError((error) =>
              of(
                new UserActions.SearchMiniShoppingListsFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          );
        }),
        withdrawOn(this.contextChange$)
      )
    );

  searchWritableShoppingLists$: Observable<
    UserActions.SearchWritableShoppingListsSuccess | UserActions.SearchWritableShoppingListsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.SearchWritableShoppingLists),
      map((action: UserActions.SearchWritableShoppingLists) => action.payload),
      switchMap((payload) => {
        return this.connector.searchWritable(payload.userId, payload.searchParams).pipe(
          map((data) => new UserActions.SearchWritableShoppingListsSuccess({ ...payload, searchResult: data })),
          catchError((error) =>
            of(
              new UserActions.SearchWritableShoppingListsFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  loadShoppingList$: Observable<UserActions.LoadShoppingListSuccess | UserActions.LoadShoppingListFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.LoadShoppingList),
      map((action: UserActions.LoadShoppingList) => action.payload),
      groupBy((payload) => payload.code),
      mergeMap((group$) =>
        group$.pipe(
          switchMap((payload) =>
            this.store.pipe(
              select(ShoppingListsSelectors.getShoppingListHasPendingProcessesFactory(payload.code)),
              map((hasPendingProcesses) => ({ payload, hasPendingProcesses }))
            )
          ),
          filter(({ hasPendingProcesses }) => !hasPendingProcesses),
          map(({ payload }) => payload),
          switchMap((payload) =>
            this.connector.load(payload.userId, payload.code).pipe(
              map((data) => new UserActions.LoadShoppingListSuccess({ ...payload, shoppingList: data })),
              catchError((error) =>
                of(
                  new UserActions.LoadShoppingListFail({
                    ...payload,
                    error: normalizeHttpError(error),
                  })
                )
              )
            )
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleFailureForLoadShoppingList$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.LoadShoppingListFail),
      map((action: UserActions.LoadShoppingListFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'shoppingLists.shoppingListNotFound_message',
              params: {
                shoppingListCode: action.payload.code,
              },
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  updateShoppingListName$: Observable<UserActions.UpdateShoppingListNameSuccess | UserActions.UpdateShoppingListNameFail> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingListActionTypes.UpdateShoppingListName),
        map((action: UserActions.UpdateShoppingListName) => action.payload),
        switchMap((payload) =>
          this.connector.updateName(payload.userId, payload.code, payload.name).pipe(
            map(() => new UserActions.UpdateShoppingListNameSuccess(payload)),
            catchError((error) =>
              of(
                new UserActions.UpdateShoppingListNameFail({
                  ...payload,
                  error: normalizeHttpError(error),
                })
              )
            )
          )
        ),
        withdrawOn(this.logoutOnlyContextChange$)
      )
    );

  handleFailureForUpdateShoppingListName$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ShoppingListActionTypes.UpdateShoppingListNameFail),
        map((action: UserActions.UpdateShoppingListNameFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'shoppingLists.shoppingListNotFound_message',
                  params: {
                    shoppingListCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            } else {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'shoppingLists.updateShoppingListNameFailure_message',
                  params: {
                    shoppingListCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            }
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
  );

  updateShoppingListDescription$: Observable<
    UserActions.UpdateShoppingListDescriptionSuccess | UserActions.UpdateShoppingListDescriptionFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.UpdateShoppingListDescription),
      map((action: UserActions.UpdateShoppingListDescription) => action.payload),
      switchMap((payload) =>
        this.connector.updateDescription(payload.userId, payload.code, payload.description).pipe(
          map(() => new UserActions.UpdateShoppingListDescriptionSuccess(payload)),
          catchError((error) =>
            of(
              new UserActions.UpdateShoppingListDescriptionFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  updateShoppingListContactPerson$: Observable<
    UserActions.UpdateShoppingListContactPersonSuccess | UserActions.UpdateShoppingListContactPersonFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.UpdateShoppingListContactPerson),
      map((action: UserActions.UpdateShoppingListContactPerson) => action.payload),
      switchMap((payload) =>
        this.connector.updateContactPerson(payload.userId, payload.code, payload.customerId).pipe(
          map(() => new UserActions.UpdateShoppingListContactPersonSuccess(payload)),
          catchError((error) =>
            of(
              new UserActions.UpdateShoppingListContactPersonFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleSuccessForUpdateShoppingListContactPerson$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.ShoppingListActionTypes.UpdateShoppingListContactPersonSuccess),
      map(
        () =>
          new GlobalMessageActions.AddMessage({
            text: {
              key: 'shoppingLists.updateContactPersonSuccess_message',
            },
            type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
          })
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForUpdateShoppingListContactPerson$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingListActionTypes.UpdateShoppingListContactPersonFail),
        map((action: UserActions.UpdateShoppingListContactPersonFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'shoppingLists.shoppingListNotFound_message',
                  params: {
                    shoppingListCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            } else {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'shoppingLists.updateContactPersonFailure_message',
                  params: {
                    shoppingListCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            }
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  handleFailureForUpdateShoppingListDescription$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(ShoppingListActionTypes.UpdateShoppingListDescriptionFail),
        map((action: UserActions.UpdateShoppingListDescriptionFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'shoppingLists.shoppingListNotFound_message',
                  params: {
                    shoppingListCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            } else {
              return new GlobalMessageActions.AddMessage({
                text: {
                  key: 'shoppingLists.updateDescriptionFailure_message',
                  params: {
                    shoppingListCode: action.payload.code,
                  },
                },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              });
            }
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  deleteShoppingList$: Observable<
    UserActions.DeleteShoppingListSuccess | UserActions.DeleteShoppingListFail | UserActions.RemoveShoppingList
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.DeleteShoppingList),
      map((action: UserActions.DeleteShoppingList) => action.payload),
      switchMap((payload) =>
        this.connector.delete(payload.userId, payload.code).pipe(
          switchMap(() => {
            return [
              new UserActions.DeleteShoppingListSuccess(payload),
              new UserActions.RemoveShoppingList({ code: payload.code }),
            ];
          }),
          catchError((error) =>
            of(
              new UserActions.DeleteShoppingListFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForDeleteShoppingList$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.DeleteShoppingListFail),
      map((action: UserActions.DeleteShoppingListFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          if (action.payload.error?.status === HttpResponseStatus.NOT_FOUND) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'shoppingLists.shoppingListNotFound_message',
                params: {
                  shoppingListCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'shoppingLists.removeShoppingListFailure_message',
                params: {
                  shoppingListCode: action.payload.code,
                },
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          }
        } else {
          return new AppActions.NoOpAction();
        }
      }),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleSuccessForRemoveShoppingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.ShoppingListActionTypes.DeleteShoppingListSuccess),
      map((action: UserActions.DeleteShoppingListSuccess) => action.payload),
      mapTo(
        new GlobalMessageActions.AddMessage({
          text: {
            key: 'shoppingLists.deleteSuccess_message',
          },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        })
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  createShoppingList$: Observable<
    UserActions.CreateShoppingListSuccess | UserActions.CreateShoppingListFail | UserActions.UpdateTempShoppingList
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.CreateShoppingList),
      map((action: UserActions.CreateShoppingList) => action.payload),
      switchMap((payload) =>
        this.connector.create(payload.userId, payload.cartCode, payload.cloneCode, payload.name, payload.description).pipe(
          switchMap((shoppingList: ShoppingList) => {
            return [
              new UserActions.CreateShoppingListSuccess({
                ...payload,
                shoppingList,
              }),
              new UserActions.UpdateTempShoppingList({
                shoppingList,
                tempCode: payload.tempCode,
              }),
            ];
          }),
          catchError((error) =>
            of(
              new UserActions.CreateShoppingListFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  createShoppingListAndAddEntry$: Observable<
    | UserActions.CreateShoppingListSuccess
    | UserActions.CreateShoppingListFail
    | UserActions.UpdateTempShoppingList
    | UserActions.AddShoppingListEntry
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.CreateShoppingListAndAddEntry),
      map((action: UserActions.CreateShoppingListAndAddEntry) => action.payload),
      switchMap((payload) =>
        this.connector.create(payload.userId, undefined, undefined, payload.name, payload.description).pipe(
          switchMap((shoppingList: ShoppingList) => {
            return [
              new UserActions.CreateShoppingListSuccess({
                ...payload,
                shoppingList,
              }),
              new UserActions.UpdateTempShoppingList({
                shoppingList,
                tempCode: payload.tempCode,
              }),
              new UserActions.AddShoppingListEntry({
                userId: payload.userId,
                code: shoppingList.code,
                articleCode: payload.articleCode,
                quantity: payload.quantity,
                unit: payload.unit,
                displayNumber: payload.displayNumber,
              }),
            ];
          }),
          catchError((error) =>
            of(
              new UserActions.CreateShoppingListFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleSuccessForCreateShoppingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.ShoppingListActionTypes.CreateShoppingListSuccess),
      mapTo(
        new GlobalMessageActions.AddMessage({
          text: { key: 'shoppingLists.createShoppingListSuccess_message' },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        })
      )
    )
  );

  handleFailureForCreateShoppingList$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.CreateShoppingListFail),
      map((action: UserActions.CreateShoppingListFail) => {
        if (!isErrorGloballyHandled(action.payload.error?.status)) {
          return new GlobalMessageActions.AddMessage({
            text: {
              key: 'shoppingLists.createShoppingListFailure_message',
            },
            type: GlobalMessageType.MSG_TYPE_ERROR,
          });
        } else {
          return new AppActions.NoOpAction();
        }
      })
    )
  );

  updateShoppingListPermissions$: Observable<
    UserActions.UpdateShoppingListPermissionsSuccess | UserActions.UpdateShoppingListPermissionsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.UpdateShoppingListPermissions),
      map((action: UserActions.UpdateShoppingListPermissions) => action.payload),
      switchMap((payload) =>
        this.connector.updatePermissions(payload.userId, payload.code, payload.permissions, payload.params).pipe(
          map(() => new UserActions.UpdateShoppingListPermissionsSuccess(payload)),
          catchError((error) =>
            of(new UserActions.UpdateShoppingListPermissionsFail({ ...payload, error: normalizeHttpError(error) }))
          )
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleSuccessForUpdateShoppingListPermissions$: Observable<GlobalMessageActions.GlobalMessageActions | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.ShoppingListActionTypes.UpdateShoppingListPermissionsSuccess),
        map((action: UserActions.UpdateShoppingListPermissionsSuccess) => action.payload),
        switchMap((payload) =>
          this.principalConfigurationService
            .getValue('sharingCartTemplatesMode')
            .pipe(map((templatesMode) => ({ payload, templatesMode })))
        ),
        map(({ payload, templatesMode }) => {
          let message;

          switch (payload.updatePermissionType) {
            case UpdatePermissionType.CONNECT_ALL:
              message = {
                key: 'shoppingLists.connectAllSuccess_message',
              };
              break;

            case UpdatePermissionType.WRITER_ALL:
              message = {
                key: 'shoppingLists.writerAllSuccess_message',
              };
              break;

            case UpdatePermissionType.CONNECT:
              if (templatesMode.value === 'SOLDTO') {
                message = {
                  key: 'shoppingLists.connectCustomerNumbersSuccess_message',
                  params: { count: payload.permissions?.length },
                };
              } else {
                message = {
                  key: 'shoppingLists.connectUsersSuccess_message',
                  params: { count: payload.permissions?.length },
                };
              }
              break;

            case UpdatePermissionType.REMOVE:
              if (templatesMode.value === 'SOLDTO') {
                message = {
                  key: 'shoppingLists.disconnectCustomerNumbersSuccess_message',
                  params: { count: payload.permissions?.length },
                };
              } else {
                message = {
                  key: 'shoppingLists.disconnectUsersSuccess_message',
                  params: { count: payload.permissions?.length },
                };
              }
              break;

            case UpdatePermissionType.WRITER:
              if (templatesMode.value === 'SOLDTO') {
                message = {
                  key: 'shoppingLists.addCustomerNumberEditorSuccess_message',
                };
              } else {
                message = {
                  key: 'shoppingLists.addUserEditorSuccess_message',
                };
              }
              break;

            case UpdatePermissionType.WRITER_REMOVE:
              if (templatesMode.value === 'SOLDTO') {
                message = {
                  key: 'shoppingLists.removeCustomerNumberEditorSuccess_message',
                };
              } else {
                message = {
                  key: 'shoppingLists.removeUserEditorSuccess_message',
                };
              }
              break;
          }

          if (message) {
            return new GlobalMessageActions.AddMessage({
              text: message,
              type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
            });
          }

          return new AppActions.NoOpAction();
        })
      )
    );

  refreshWithoutProcesses$: Observable<UserActions.LoadShoppingList | UserActions.SearchMiniShoppingLists> = createEffect(() =>
    this.actions$.pipe(
      ofType<
        | UserActions.AddShoppingListEntrySuccess
        | UserActions.RemoveShoppingListEntrySuccess
        | UserActions.UpdateShoppingListEntrySuccess
        | UserActions.ReplaceShoppingListEntrySuccess
      >(
        ShoppingListEntryActionTypes.AddShoppingListEntrySuccess,
        ShoppingListEntryActionTypes.RemoveShoppingListEntrySuccess,
        ShoppingListEntryActionTypes.UpdateShoppingListEntrySuccess,
        ShoppingListEntryActionTypes.ReplaceShoppingListEntrySuccess
      ),
      map((action) => action.payload),
      concatMap((payload) => [
        new UserActions.LoadShoppingList({
          userId: payload.userId,
          code: payload.code,
        }),
        new UserActions.SearchMiniShoppingLists({
          ...payload,
          searchParams: {
            key: 'add-to-shopping-list',
            page: 0,
          },
        }),
      ])
    )
  );

  resetShoppingListOnSiteContextChange$: Observable<UserActions.ResetShoppingLists> = createEffect(() =>
    this.actions$.pipe(
      ofType(LanguagesActionTypes.LanguageChange, CurrenciesActionTypes.CurrencyChange),
      mergeMap(() => {
        return [new UserActions.ResetShoppingLists()];
      })
    )
  );

  updateTempShoppingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.UpdateTempShoppingList),
      map((action: UserActions.UpdateTempShoppingList) => {
        return new UserActions.RemoveShoppingList({ code: action.payload.tempCode });
      }),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  loadShoppingListPrincipals$: Observable<
    UserActions.LoadShoppingListPrincipalsSuccess | UserActions.LoadShoppingListPrincipalsFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(ShoppingListActionTypes.LoadShoppingListPrincipals),
      switchMap((payload: UserActions.LoadShoppingListPrincipals) =>
        this.connector.getPrincipals(payload.userId, payload.code, payload.searchParams).pipe(
          map(
            (data) => new UserActions.LoadShoppingListPrincipalsSuccess(payload.userId, payload.code, data, payload.searchParams)
          ),
          catchError((error) =>
            of(
              new UserActions.LoadShoppingListPrincipalsFail(
                payload.userId,
                payload.code,
                normalizeHttpError(error),
                payload.searchParams
              )
            )
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  constructor(
    private actions$: Actions,
    private connector: ShoppingListConnector,
    private store: Store<StateWithUser>,
    private principalConfigurationService: PrincipalConfigurationService
  ) {}
}
